import React, { useEffect, useState } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { User, FloppyDisk } from '@phosphor-icons/react';
import WorkflowStepper from '@/components/WorkflowStepper';
import FieldHelp from '@/components/FieldHelp';

const MARITAL_STATUS = [
  { value: 'single', label: 'Single' },
  { value: 'married', label: 'Married' },
  { value: 'partnered', label: 'Partnered / Domestic Partnership' },
  { value: 'divorced', label: 'Divorced' },
  { value: 'widowed', label: 'Widowed' },
];

const FILING_STATUS = [
  { value: 'single', label: 'Single' },
  { value: 'head_of_household', label: 'Head of Household' },
  { value: 'married_filing_jointly', label: 'Married Filing Jointly' },
  { value: 'married_filing_separately', label: 'Married Filing Separately' },
  { value: 'qualifying_widow', label: 'Qualifying Widow(er)' },
];

// All 50 states + DC for the dropdown
const STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY',
  'LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH',
  'OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
];

const FinancialProfilePage = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/financial-profile')
      .then((res) => { if (!cancelled) setProfile(res.data); })
      .catch(() => { if (!cancelled) toast.error('Could not load your profile'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const update = (field, value) => setProfile((p) => ({ ...p, [field]: value }));

  const save = async () => {
    if (!profile) return false;
    setSaving(true);
    try {
      const payload = {
        age: Number(profile.age) || 0,
        retirement_age: Number(profile.retirement_age) || 65,
        life_expectancy: Number(profile.life_expectancy) || 90,
        marital_status: profile.marital_status || 'single',
        tax_filing_status: profile.tax_filing_status || 'single',
        dependents: Number(profile.dependents) || 0,
        state: profile.state || 'CA',
      };
      const { data } = await api.put('/financial-profile', payload);
      setProfile(data);
      toast.success('Profile saved');
      return true;
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save your profile.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-[#4A5568]">Loading your profile…</div>;
  }
  if (!profile) {
    return <div className="text-[#C53030]">Could not load your profile.</div>;
  }

  return (
    <div className="space-y-8" data-testid="financial-profile-page">
      <div>
        <h1 className="cabinet-grotesk text-3xl font-medium text-[#0A1128]">Personal Profile</h1>
        <p className="text-[#4A5568] mt-1">Step 1 of your setup. These details power tax projections, retirement planning, and the Wealth Health Score.</p>
      </div>

      <section className="bg-white border border-slate-200 rounded-md shadow-sm dashboard-card overflow-hidden">
        <header className="px-6 py-4 border-b border-slate-200 bg-[#FDFCFB] flex items-center gap-3">
          <User size={20} weight="duotone" className="text-[#C5A059]" />
          <h2 className="text-base font-semibold text-[#1A202C]">About You</h2>
        </header>
        <form
          onSubmit={(e) => { e.preventDefault(); save(); }}
          className="p-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5"
          data-testid="financial-profile-form"
        >
          <div>
            <Label htmlFor="age" className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2 flex items-center gap-1.5">
              Current Age
              <FieldHelp testid="help-age" title="Current Age" body="Your current age in years. Used to gauge retirement readiness and project future balances." suggestion="Enter your actual age (whole number)." />
            </Label>
            <Input id="age" type="number" min="18" max="100"
              data-testid="profile-age-input"
              value={profile.age ?? ''}
              onChange={(e) => update('age', e.target.value)}
              className="border-slate-300"
            />
          </div>

          <div>
            <Label htmlFor="retirement_age" className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2 flex items-center gap-1.5">
              Retirement Goal Age
              <FieldHelp testid="help-retirement-age" title="Retirement Goal Age" body="The age at which you'd like to stop working and start drawing income from retirement savings." suggestion="Most U.S. workers target 65–67 (full Social Security retirement age)." />
            </Label>
            <Input id="retirement_age" type="number" min="40" max="80"
              data-testid="profile-retirement-age-input"
              value={profile.retirement_age ?? ''}
              onChange={(e) => update('retirement_age', e.target.value)}
              className="border-slate-300"
            />
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2 flex items-center gap-1.5">
              Marital Status
              <FieldHelp testid="help-marital" title="Marital Status" body="Used to default your tax filing status and to combine with a partner in Household planning." suggestion="Pick your current legal status." />
            </Label>
            <Select value={profile.marital_status || 'single'} onValueChange={(v) => update('marital_status', v)}>
              <SelectTrigger data-testid="profile-marital-select" className="border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MARITAL_STATUS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2 flex items-center gap-1.5">
              Tax Filing Status
              <FieldHelp testid="help-filing" title="Tax Filing Status" body="The status you'll use on your federal tax return. Affects bracket calculations on Tax Optimizer, Roth Conversion, and Social Security tools." suggestion="If unsure, pick the status you used on last year's return." />
            </Label>
            <Select value={profile.tax_filing_status || 'single'} onValueChange={(v) => update('tax_filing_status', v)}>
              <SelectTrigger data-testid="profile-filing-select" className="border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FILING_STATUS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="dependents" className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2 flex items-center gap-1.5">
              Dependents
              <FieldHelp testid="help-dependents" title="Dependents" body="Number of children or other tax dependents you claim. Used for tax credit estimates." suggestion="Enter the count from line 7 of your Form 1040, or 0 if none." />
            </Label>
            <Input id="dependents" type="number" min="0" max="20"
              data-testid="profile-dependents-input"
              value={profile.dependents ?? 0}
              onChange={(e) => update('dependents', e.target.value)}
              className="border-slate-300"
            />
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2 flex items-center gap-1.5">
              State
              <FieldHelp testid="help-state" title="State of Residence" body="Your state of legal residence. Used for state tax calculations on Tax Optimizer." suggestion="Pick the state where you file your state income tax." />
            </Label>
            <Select value={profile.state || 'CA'} onValueChange={(v) => update('state', v)}>
              <SelectTrigger data-testid="profile-state-select" className="border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {STATES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-2 pt-2">
            <Button
              type="submit"
              data-testid="profile-save-button"
              disabled={saving}
              className="bg-[#0A1128] text-white hover:bg-[#152145] rounded-md py-2.5 px-5 text-sm font-medium transition-colors flex items-center gap-2"
            >
              <FloppyDisk size={16} />
              {saving ? 'Saving…' : 'Save profile'}
            </Button>
          </div>
        </form>
      </section>

      <WorkflowStepper onContinue={save} continueLabel="Save & Continue to Income" />
    </div>
  );
};

export default FinancialProfilePage;
